import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CancelIcon from "@mui/icons-material/Cancel";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import CustomDropdownField from "./CustomDropdownFiled";
import CustomTextField from "./CustomTextField";
import CustomDatePicker from "./CustomDatePicker";
import { useModalStyles } from "./useModalStyles";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import {
  fetchActivePracticeProvidersData,
  usePracticeProvidersSlice,
} from "../../redux/slice/practice/practiceProvidersSlice";
import {
  AddPatientByEMRCheck,
  addPatientThorughEMR,
  getSurgicalTemplateBasedOnProvider,
  resetSurgicalTemplateOptions,
  usePatientReducer,
} from "../../redux/slice/patient/patientSlice";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../redux/slice/codetableSlice";
import { Option } from "../operation/practice/PracticeEditView";
import { fullName } from "../../utils/Utility";
import {
  MSG_PATIENT_NOT_FOUND_IN_EMR,
  PRACTICE_TYPE,
  RESPONSE_CODES,
} from "../../constants/applicationConstants";
import OptionsPopup, { OptionPopupObjectProps } from "./OptionsPopup";

interface PracticeProps {
  open: boolean;
  onClose: () => void;
  practiceData?: any;
}

export const providerInitialState: any = {
  providerId: undefined,
  practiceId: undefined,
  title: "",
  firstName: "",
  lastName: "",
  middleName: "",
  npi: "",
  ptan: "",
  taxId: "",
  email: "",
};

const AddPatientEmrPopup: React.FC<PracticeProps> = ({
  open,
  onClose,
  practiceData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { practiceProvidersInfo } = usePracticeProvidersSlice();
  const { surgicalTemplateOptions } = usePatientReducer();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();

  const [eMRIntegrationProviderList, setEMRIntegrationProviderList] = useState<
    CodeTable[]
  >([]);
  const [showOptionPopup, setShowOptionsPopup] =
    useState<OptionPopupObjectProps>({ message: "", show: false });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<AddPatientByEMRCheck>({ mode: "onSubmit" });

  const watchedSpecialization = watch("practiceSpecialization");

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(resetSurgicalTemplateOptions(null));
  }, []);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sorted = [
        ...getCodeTableValuesByTableName(
          allCodeTablesValuesMap,
          "EMRIntegrationProvider"
        ),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setEMRIntegrationProviderList(sorted);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (getValues("practiceId")) {
      handlePracticeChange({ value: getValues("practiceId") });
      dispatch(resetSurgicalTemplateOptions(null));
    }
  }, [getValues("practiceId"), getValues("providerId")]);

  useEffect(() => {
    if (getValues("providerId")) {
      handleProviderChange({ value: getValues("providerId") });
      dispatch(resetSurgicalTemplateOptions(null));
    }
  }, [getValues("providerId")]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handlePracticeChange = (data: any) => {
    if (data?.value !== undefined && data?.value !== null) {
      dispatch(
        fetchActivePracticeProvidersData({
          productId: String(data?.value),
          onlyActiveRecords: true,
        })
      );
      const practiceEmrData = practiceData?.find(
        (practice: any) => practice.practiceId === data?.value
      );
      setValue(
        "emrProviderPracticeId",
        practiceEmrData?.emrProviderPracticeId ?? null
      );
      setValue("emrIntegrationProvider", practiceEmrData?.emrIntegrationProvider);
      setValue("practiceSpecialization", practiceEmrData?.specialization);
    } else {
      setValue("emrIntegrationProvider", null);
      dispatch(resetSurgicalTemplateOptions(null));
      reset({ emrProviderPracticeId: null });
    }
  };

  const handleProviderChange = (data: any) => {
    if (data?.value !== undefined && data?.value !== null) {
      dispatch(getSurgicalTemplateBasedOnProvider(data?.value));
    } else {
      setValue("providerId", null);
      setValue("providerTemplateId", null);
      dispatch(resetSurgicalTemplateOptions(null));
    }
  };

  const cancelOptionPopup = () =>
    setShowOptionsPopup({ message: "", show: false });

  const handleClose = () => {
    onClose();
    setTimeout(() => reset(), 300);
  };

  // ── Memos ─────────────────────────────────────────────────────────────────

  const practiceOptions = useMemo(() => {
    const options: Option[] = [];
    if (!practiceData?.length) return options;

    if (practiceData.length === 1) {
      options.push({
        value: practiceData[0].practiceId,
        label: practiceData[0].practiceName,
      });
      setValue("practiceId", Number(options[0].value));
      setValue("emrProviderPracticeId", practiceData[0].emrProviderPracticeId);
      setValue("emrIntegrationProvider", practiceData[0].emrIntegrationProvider);
      return options;
    }

    practiceData.forEach((practice: any) => {
      options.push({ value: practice.practiceId, label: practice.practiceName });
    });
    return options;
  }, [practiceData, setValue]);

  const practiceProvidersOptions = useMemo(() => {
    const isSurgical =
      getValues("practiceSpecialization") === PRACTICE_TYPE.SURGICAL ||
      getValues("practiceSpecialization") ===
        PRACTICE_TYPE.WOUND_CARE_AND_SURGICAL;

    const options: { label: string; value: number }[] = [];
    practiceProvidersInfo?.forEach((opt: any) => {
      options.push({
        label: `${opt.title ?? ""} ${opt.firstName ?? ""} ${
          opt.middleName ? opt.middleName + " " : ""
        }${opt.lastName ?? ""}`.trim(),
        value: opt.providerId,
      });
    });

    if (isSurgical && options.length === 1) {
      setValue("providerId", options[0].value);
      setValue("providerName", options[0].label);
    }
    return options;
  }, [practiceProvidersInfo, watchedSpecialization, PRACTICE_TYPE]);

  const surgicalTemplateOptionData = useMemo(() => {
    if (!surgicalTemplateOptions?.length) return [];
    return surgicalTemplateOptions
      .filter((opt: any) => opt.inactiveDate === null)
      .map((opt: any) => ({ label: opt.procedureName, value: opt.templateId }));
  }, [surgicalTemplateOptions]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = (data: AddPatientByEMRCheck) => {
    const EMRData = {
      practiceEMRId: data?.emrProviderPracticeId,
      firstName: data?.firstName,
      lastName: data?.lastName,
      dateOfBirth: moment(data?.dateOfBirth?.toString()).format("MM/DD/YYYY"),
      dateOfSurgery: moment(data?.dateOfSurgery?.toString()).format("MM/DD/YYYY"),
      practiceId: data?.practiceId,
      providerId: data?.providerId,
      providerName: getValues("providerName"),
      patientAddedFromEmrPopup: true,
      providerTemplateId: data?.providerTemplateId,
    };

    if (EMRData.practiceEMRId) {
      dispatch(addPatientThorughEMR(EMRData)).then((res: any) => {
        if (
          res?.payload?.code === RESPONSE_CODES.SUCCESS_CODE &&
          res?.payload?.data?.patientId
        ) {
          const patientName = fullName(
            null,
            res?.payload?.data?.firstName,
            res?.payload?.data?.middleName,
            res?.payload?.data?.lastName
          );
          navigate("/patientDetails", {
            state: {
              patientDetails: {
                patientId: res?.payload?.data?.patientId,
                patientName,
                dob: res?.payload?.birthDate,
              },
              alert: {
                message:
                  "Patient details are received. Please proceed to create IVR.",
                showButton: true,
                buttonText: "CREATE IVR",
              },
            },
          });
        } else if (
          res?.payload?.code === RESPONSE_CODES.SUCCESS_CODE &&
          res?.payload?.message === MSG_PATIENT_NOT_FOUND_IN_EMR
        ) {
          setShowOptionsPopup({
            message:
              "Patient not found in EMR, please enter data manually by clicking on Add Patient.",
            show: true,
            variant: "error",
            buttons: [
              {
                name: "Add Patient",
                color: "success",
                onClick: () =>
                  navigate("/editPatient", {
                    state: {
                      patientDetails: {
                        isPatientEdit: false,
                        patientId: null,
                        patientData: EMRData,
                      },
                    },
                  }),
              },
              { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
            ],
          });
        }
      });
    } else {
      navigate("/editPatient", {
        state: {
          patientDetails: {
            isPatientEdit: false,
            patientId: null,
            patientData: EMRData,
          },
        },
      });
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const isSurgical =
    watchedSpecialization === PRACTICE_TYPE.SURGICAL ||
    watchedSpecialization === PRACTICE_TYPE.WOUND_CARE_AND_SURGICAL;

  const providerDisabled =
    (isSurgical && practiceProvidersOptions?.length === 1) ||
    !getValues("practiceId");

  // ── Sub-components ────────────────────────────────────────────────────────

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={cancelOptionPopup}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {/* ── Header ── */}
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <UserPlus size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                fontWeight={700}
                sx={{
                  fontSize: "1.1rem",
                  color: tokens.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                Add Patient
              </Typography>

              {getValues("emrIntegrationProvider") &&
                getValues("emrProviderPracticeId") && (
                  <Tooltip
                    arrow
                    placement="right"
                    title={
                      <>
                        <Typography variant="body2">
                          EMR Provider: {getValues("emrIntegrationProvider")}
                        </Typography>
                        <Typography variant="body2">
                          EMR Practice ID: {getValues("emrProviderPracticeId")}
                        </Typography>
                      </>
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.2,
                        borderRadius: "6px",
                        cursor: "pointer",
                        userSelect: "none",
                        backgroundColor: "rgba(251,146,60,0.12)",
                        border: "1px solid rgba(251,146,60,0.3)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#f97316",
                          letterSpacing: "0.04em",
                        }}
                      >
                        EMR
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
            </Box>

            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              Fill in the patient details below
            </Typography>
          </Box>

          <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          {/* Practice Name */}
          <Box>
            <FieldLabel label="Practice Name" required />
            <Box sx={dropdownWrapSx(!!errors.practiceId)}>
              <CustomDropdownField
                control={control}
                options={practiceOptions}
                label=""
                name="practiceId"
                rules={{ required: "Practice Name is required." }}
                disabled={practiceOptions?.length === 1}
                onChangeSelect={handlePracticeChange}
                size="small"
                placeholderText="Select a practice..."
                sortOptions={false}
              />
            </Box>
          </Box>

          {/* First Name / Last Name */}
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
          >
            <Box>
              <FieldLabel label="First Name" required />
              <CustomTextField
                control={control}
                controllerName="firstName"
                label=""
                size="small"
                placeholder="Enter first name"
                rules={{ required: "First Name is required." }}
                sx={inputFieldSx(!!errors.firstName)}
              />
            </Box>
            <Box>
              <FieldLabel label="Last Name" required />
              <CustomTextField
                control={control}
                controllerName="lastName"
                label=""
                size="small"
                placeholder="Enter last name"
                rules={{ required: "Last Name is required." }}
                sx={inputFieldSx(!!errors.lastName)}
              />
            </Box>
          </Box>

          {/* Date of Birth */}
          <Box>
            <FieldLabel label="Date of Birth" required />
            <Box sx={datepickerWrapSx(!!errors.dateOfBirth)}>
              <CustomDatePicker
                label=""
                control={control}
                controllerName="dateOfBirth"
                rules={{ required: "Date Of Birth is required." }}
                size="small"
                fullWidth
                isDisableFuture
              />
            </Box>
          </Box>

          {/* Date of Surgery (surgical practices only) */}
          {isSurgical && (
            <Box>
              <FieldLabel label="Date of Surgery" required />
              <Box sx={datepickerWrapSx(!!errors.dateOfSurgery)}>
                <CustomDatePicker
                  label=""
                  control={control}
                  controllerName="dateOfSurgery"
                  rules={{ required: "Date Of Surgery is required." }}
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          )}

          {/* Provider */}
          <Box>
            <FieldLabel label="Provider" required={isSurgical} />
            <Box sx={dropdownWrapSx(!!errors.providerId)}>
              <CustomDropdownField
                control={control}
                options={practiceProvidersOptions}
                label=""
                name="providerId"
                rules={isSurgical ? { required: "Provider is required." } : {}}
                size="small"
                placeholderText="Select a provider..."
                sortOptions={false}
                disabled={providerDisabled}
                onChangeSelect={handleProviderChange}
              />
            </Box>
          </Box>

          {/* Provider Template (when templates are available) */}
          {surgicalTemplateOptionData.length > 0 && (
            <Box>
              <FieldLabel label="Provider Template" required={isSurgical} />
              <Box sx={dropdownWrapSx(!!errors.providerTemplateId)}>
                <CustomDropdownField
                  control={control}
                  options={surgicalTemplateOptionData}
                  label=""
                  name="providerTemplateId"
                  rules={
                    isSurgical
                      ? { required: "Surgical Template is required." }
                      : {}
                  }
                  size="small"
                  placeholderText="Select a template..."
                  sortOptions={true}
                  disabled={!getValues("providerId")}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                <PersonAddAlt1Icon sx={{ fontSize: "1rem !important" }} />
              }
              onClick={handleSubmit(onSubmit)}
              sx={primaryBtnSx}
            >
              Add Patient
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default AddPatientEmrPopup;